<script setup lang="ts">
import type { IUiPaginationProps } from './UiPagination.types'

const props = withDefaults(defineProps<IUiPaginationProps>(), {
  total: 0,
  itemsPerPage: 10,
})

const modelValue = defineModel<number>({ default: 1 })

const DOTS = '...'

const pageCount = computed(() => props.pageCount ?? Math.max(1, Math.ceil(props.total / props.itemsPerPage)))

const canPrev = computed(() => modelValue.value > 1)
const canNext = computed(() => modelValue.value < pageCount.value)

const range = (start: number, end: number) => Array.from({ length: end - start + 1 }, (_, i) => start + i)

// Page numbers to render: first, last and a window around the current page, with ellipsis for the gaps.
// A gap of a single page shows that page instead of an ellipsis.
// Near the edges the window is widened, so the number of slots (pages + ellipsis) stays constant.
const buildPages = (siblings: number): (number | string)[] => {
  const total = pageCount.value
  const current = modelValue.value
  const edge = siblings * 2 + 3

  if (total <= edge + 2) return range(1, total)

  let start = Math.max(current - siblings, 1)
  let end = Math.min(current + siblings, total)

  if (start <= 2) {
    start = 1
    end = Math.max(end, edge)
  }

  if (end >= total - 1) {
    start = Math.min(start, total - edge + 1)
    end = total
  }

  const left = start > 3 ? [1, DOTS] : range(1, start - 1)
  const right = end < total - 2 ? [DOTS, total] : range(end + 1, total)

  return [...left, ...range(start, end), ...right]
}

const pageSets = computed(() => [
  { variant: 'compact', pages: buildPages(0) },
  { variant: 'wide', pages: buildPages(1) },
])

const goTo = (page: number) => {
  modelValue.value = Math.min(Math.max(page, 1), pageCount.value)
}

const onKeydown = (e: KeyboardEvent) => {
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return

  e.preventDefault()
  goTo(modelValue.value + (e.key === 'ArrowRight' ? 1 : -1))
}
</script>

<template>
  <nav
    class="uiPagination"
    :aria-label="$t('common.pagination.label')"
    @keydown="onKeydown"
  >
    <button
      v-if="canPrev"
      type="button"
      class="uiPagination__cell uiPagination__arrow uiPagination__arrow--prev"
      :aria-label="$t('common.pagination.prev')"
      @click="goTo(modelValue - 1)"
    >
      <span
        class="uiPagination__icon"
        aria-hidden="true"
      />
    </button>

    <ul
      v-for="set in pageSets"
      :key="set.variant"
      :class="['uiPagination__list', `uiPagination__list--${set.variant}`]"
      role="list"
    >
      <li
        v-for="(page, index) in set.pages"
        :key="index"
        class="uiPagination__item"
      >
        <span
          v-if="page === DOTS"
          class="uiPagination__ellipsis"
          aria-hidden="true"
        >
          {{ DOTS }}
        </span>

        <button
          v-else
          type="button"
          :class="[
            'uiPagination__cell',
            'uiPagination__page',
            { 'uiPagination__page--active': page === modelValue }
          ]"
          :aria-label="`${$t('common.pagination.goToPage')} ${page}`"
          :aria-current="page === modelValue ? 'page' : undefined"
          @click="goTo(page as number)"
        >
          {{ page }}
        </button>
      </li>
    </ul>

    <button
      v-if="canNext"
      type="button"
      class="uiPagination__cell uiPagination__arrow uiPagination__arrow--next"
      :aria-label="$t('common.pagination.next')"
      @click="goTo(modelValue + 1)"
    >
      <span
        class="uiPagination__icon"
        aria-hidden="true"
      />
    </button>

    <span
      class="uiPagination__status"
      aria-live="polite"
    >
      {{ $t('common.pagination.status', { page: modelValue, total: pageCount }) }}
    </span>
  </nav>
</template>

<style lang="scss" scoped>
.uiPagination {
  $root: &;

  display: flex;
  align-items: center;
  gap: 0.4rem;

  &__list {
    display: contents;
    list-style: none;
  }

  &__list--wide {
    display: none;
  }

  &__item {
    display: flex;
    flex: 0 1 4rem;
  }

  &__cell,
  &__ellipsis {
    @include body-2;
    display: flex;
    flex: 0 1 4rem;
    justify-content: center;
    align-items: center;
    aspect-ratio: 1;
    color: $text-400;
    background-color: $white;
    border: 0.1rem solid $gray-400;
    border-radius: 1.6rem;
  }

  &__cell {
    cursor: pointer;
    transition:
      color 0.18s ease,
      border-color 0.18s ease,
      background-color 0.18s ease;

    @media (hover: hover) {
      &:hover {
        background-color: $gray-100;
        border-color: $text-400;
      }
    }

    &:focus-visible {
      border-color: $purple-400;
      outline: 0.2rem solid $purple-400;
      outline-offset: 0.2rem;
    }
  }

  &__page--active {
    border-color: $text-400;
  }

  &__arrow {
    border-radius: 50%;
  }

  &__icon {
    width: 2.4rem;
    height: 2.4rem;
    background-image: url('/icons/icn_arrow_right.svg');
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
  }

  &__arrow--prev #{$root}__icon {
    transform: scaleX(-1);
  }

  &__status {
    position: absolute;
    width: 0.1rem;
    height: 0.1rem;
    margin: -0.1rem;
    padding: 0;
    overflow: hidden;
    white-space: nowrap;
    border: 0;
    clip: rect(0, 0, 0, 0);
  }

  @include atXXS {
    gap: 0.8rem;

    &__list--compact {
      display: none;
    }

    &__list--wide {
      display: contents;
    }

    &__item,
    &__cell,
    &__ellipsis {
      flex-basis: 4.8rem;
    }
  }
}
</style>
